import {Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDialogRef} from "@angular/material/dialog";
import {CurrentTenantService} from "../../../../services/front/current-tenant.service";
import {NewApplicationService} from "../../../../services/back/new-application.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {ApplicationInstance, ExtensionAppListForm} from "../../../../services/model/new-application.model";
import {Subscription, tap} from "rxjs";
import {FormControl} from "@angular/forms";
import {ChipsModule} from "../../../global/chips/chips/chips.module";
import {DesignSystemModule} from "../../../design-system/design-system.module";
import {NgForOf, NgIf} from "@angular/common";
import {MatCheckbox, MatCheckboxChange} from "@angular/material/checkbox";
import {SnackbarService} from "../../../../services/front/snackbar.service";
import {DashboardPageLoaderComponent} from "../../../global/title-content-loader/dashboard-page-loader.component";
import {MatMenu, MatMenuTrigger} from "@angular/material/menu";

@Component({
  selector: 'app-extension-app-list',
	imports: [
		ChipsModule,
		TranslateModule,
		DesignSystemModule,
		NgIf,
		MatCheckbox,
		NgForOf,
		DashboardPageLoaderComponent,
		MatMenu,
		MatMenuTrigger,
	],
  templateUrl: './extension-app-list.component.html',
  styleUrl: './extension-app-list.component.scss'
})
export class ExtensionAppListComponent implements OnInit, OnDestroy {
	constructor(private dialog: MatDialogRef<ExtensionAppListComponent>) {
	}
	private currentTenantService = inject(CurrentTenantService);
	private applicationService = inject(NewApplicationService);
	private snackbarService = inject(SnackbarService);
	private translate = inject(TranslateService);

	tenantId: string;

	applications: ApplicationInstance[] = [];
	filteredApplications: ApplicationInstance[] = [];

	appListForm: ExtensionAppListForm[] = [];

	@ViewChild('menu', { static: false }) menu: MatMenuTrigger;

	initialized: boolean = false;
	_loading: boolean;

	searchInput: FormControl = new FormControl('');

	subscription: Subscription = new Subscription();

	ngOnInit() {
		this.subscription.add(this.currentTenantService.getCurrentTenantChanges()
			.pipe(tap((tenant) => this.tenantId = tenant.configuration.id))
			.subscribe(() => this.initialize()));

		this.subscription.add(this.searchInput.valueChanges
			.pipe(tap((value: string) => this.filterApplications(value)))
			.subscribe());
	}

	initialize() {
		this.switchLoading(true);
		this.subscription.add(this.applicationService.getAllApplicationInstance(this.tenantId)
			.pipe(tap((applications) => {
				this.applications = applications;
				this.filteredApplications = applications;
				this.switchLoading(false);
				this.initialized = true;
			}))
			.subscribe());
	}

	filterApplications(value: string) {
		if (value) {
			this.filteredApplications = this.applications.filter((application) => {
				return application.name.toLowerCase().includes(value.toLowerCase());
			});
		} else {
			this.filteredApplications = this.applications;
		}
	}

	onCheckChange(application: ApplicationInstance, event: MatCheckboxChange) {
		application.displayedInExtension = event.checked;
		this.syncAppListForm();
	}

	selectAll() {
		this.searchInput.reset('', { emitEvent: false });

		this.filteredApplications.forEach(app => {
			app.displayedInExtension = true;
		});

		this.syncAppListForm();
		this.menu.closeMenu();
	}

	deselectAll() {
		this.searchInput.reset('', { emitEvent: false });

		this.filteredApplications.forEach(app => {
			app.displayedInExtension = false;
		});

		this.syncAppListForm();
		this.menu.closeMenu();
	}

	private syncAppListForm() {
		this.appListForm = this.applications.map(app => ({
			id: app.applicationId,
			displayed: app.displayedInExtension
		}));
	}

	onDismiss() {
		this.dialog.close();
	}

	updateApplicationsDisplayed() {
		this.switchLoading(true);
		this.subscription.add(this.applicationService.updateApplicationsDisplayed(this.tenantId, this.appListForm)
			.pipe(tap((result) => {
				this.switchLoading(false);
				this.displayUpdatedSucceed(result)
				this.dialog.close();
			}))
			.subscribe());
	}

	displayUpdatedSucceed(updateSucceed: boolean): void {
		if (updateSucceed) {
			this.snackbarService.show(this.translate.instant('page.application.detail.update.success'));
		} else {
			this.snackbarService.show(this.translate.instant('page.application.detail.update.fail'), 'danger-snack');
		}
	}

	switchLoading(loading: boolean) {
		this._loading = loading;
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}
}
